import * as ko from 'knockout';
import page from 'page';

import { IslandHandle, IslandName, IslandPropsFor, configureIslandRouter, mountIsland } from 'react-islands';

import { MaybeKO } from './utils/ko_utils';

const HOST_CLASS = 'react-island';

let currentRouteParams: { [key: string]: string } = {};

/**
 * Publishes route params for islands reading them through the `next/router` shim. Must be called
 * from the concrete route's own handler.
 *
 * Each is published under both spellings — page.js says `:trialId`, Next says `[trial-id]`.
 */
export function setCurrentRouteParams(params?: { [key: string]: string }): void {
  const published: { [key: string]: string } = {};
  const source = params || {};

  for (const [key, value] of Object.entries(source)) {
    const kebab = key.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

    published[key] = value;
    published[kebab] = value;
  }

  currentRouteParams = published;
}

export interface IslandParams<N extends IslandName> {
  props?: MaybeKO<IslandPropsFor<N>>;
}

/**
 * A view model's props for an island, typed from its contract in frontend/src/islands/props.ts:
 *
 *   collapseAll = islandProps('collapse-all-button', { onClick: () => this.collapse() });
 */
export function islandProps<N extends IslandName>(
  island: N,
  initial: IslandPropsFor<N>
): KnockoutObservable<IslandPropsFor<N>> {
  void island; // only present so the observable's type is inferred rather than hand-written

  return ko.observable(initial);
}

/**
 * Registers a React island as a Knockout component under its own name, so templates use it like
 * any other: `<collapse-all-button params="props: collapseAll">`.
 */
function registerReactIsland<N extends IslandName>(island: N): void {
  ko.components.register(island, {
    // ko owns the custom element, React owns this inner div — so ko's DOM cleanup and React's
    // reconciler never touch the same node.
    template: '<div class="' + HOST_CLASS + '"></div>',
    viewModel: {
      createViewModel: (params: IslandParams<N>, componentInfo: KnockoutComponentTypes.ComponentInfo) => {
        const element = componentInfo.element as Element;
        const host = element.querySelector('.' + HOST_CLASS) as HTMLElement;

        const props = ko.computed<IslandPropsFor<N>>(() => {
          // Omitted `props` is valid for an island with no required fields.
          if (params.props === undefined) {
            return {} as IslandPropsFor<N>;
          }

          return ko.unwrap(params.props);
        });

        const handle: IslandHandle<IslandPropsFor<N>> = mountIsland(host, island, props());
        const subscription = props.subscribe((next: IslandPropsFor<N>) => handle.update(next));

        ko.utils.domNodeDisposal.addDisposeCallback(element, () => {
          subscription.dispose();
          props.dispose();
          handle.unmount();
        });

        return {};
      },
    },
  });
}

configureIslandRouter({
  push: (url: string) => page.show(url),
  replace: (url: string) => page.redirect(url),
  back: () => history.back(),
  getRouteParams: () => currentRouteParams,
});

registerReactIsland('collapse-all-button');
registerReactIsland('trial-info-header');
registerReactIsland('trials-list');
registerReactIsland('traits-list');
registerReactIsland('dimensions-list');
registerReactIsland('dimension-records');
registerReactIsland('groups-list');
registerReactIsland('crop-varieties-list');
registerReactIsland('document-tags-list');
registerReactIsland('scheduled-visits-list');
registerReactIsland('trial-types-list');
registerReactIsland('attribute-choices-list');
registerReactIsland('countries-list');
registerReactIsland('administrative-regions-list');
registerReactIsland('agroclimatic-regions-list');
registerReactIsland('trait-categories-list');
registerReactIsland('users-list');
registerReactIsland('trial-activity-status-list');
registerReactIsland('trait-tags-list');
registerReactIsland('organizations-list');
registerReactIsland('sites-list');
registerReactIsland('crops-list');
registerReactIsland('stages-list');
registerReactIsland('seasons-list');
registerReactIsland('names-list');
registerReactIsland('programs-list');
registerReactIsland('tpps-list');
registerReactIsland('drivers-list');
registerReactIsland('measurement-types-list');
registerReactIsland('organization-types-list');
registerReactIsland('report-templates-list');
registerReactIsland('unit-groups-settings');
registerReactIsland('unit-group-field');
